import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SectionHeading } from "@/components/bespoke/section-heading";
import type { ProjectPolicies, UpdateProjectPolicies } from "@zworks/console-contracts";
import { useState, type FormEvent } from "react";
import { LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { projectPoliciesQuery, updateProjectPolicies } from "@/lib/api";
import { SettingsFailure } from "./settings-feedback";
import { settingsSelectClass } from "./general-settings-section";

/**
 * Marks a field that is saved and audited but not yet applied anywhere. Only a field whose effect
 * is wired in the server carries no such label.
 */
function RecordedOnly() {
  return <Badge variant="muted">Recorded only</Badge>;
}

function editable(value: ProjectPolicies): UpdateProjectPolicies {
  return {
    requireDeployApproval: value.requireDeployApproval,
    productionTwoApprovers: value.productionTwoApprovers,
    productionActionTypes: [...value.productionActionTypes],
    approvalExpiryHours: value.approvalExpiryHours,
    noSelfApproval: value.noSelfApproval,
    pauseAtCap: value.pauseAtCap,
    alertThresholds: [...value.alertThresholds],
    modelAccess: value.modelAccess,
    allowedModels: [...value.allowedModels],
    readOnlyDefault: value.readOnlyDefault,
    maxParallelRuns: value.maxParallelRuns,
    sandboxMinutes: value.sandboxMinutes,
  };
}

/** The approvals policy section: who decides deploys, production quorum, and expiry. */
export function ApprovalsPolicySection({
  projectId,
  isOwner,
}: {
  projectId: string;
  isOwner: boolean;
}) {
  const queryClient = useQueryClient();
  const query = useQuery(projectPoliciesQuery(projectId));
  const [draft, setDraft] = useState<UpdateProjectPolicies | null>(null);
  const [typesText, setTypesText] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (input: UpdateProjectPolicies) => updateProjectPolicies(projectId, input),
    onSuccess: (value) => {
      setDraft(null);
      setTypesText(null);
      setNotice("Approval policy saved and audited.");
      queryClient.setQueryData(projectPoliciesQuery(projectId).queryKey, value);
    },
  });

  if (query.isPending) return <LoadingState label="Loading approval policy" rows={4} />;
  if (query.isError)
    return <SettingsFailure error={query.error} retry={() => void query.refetch()} />;
  const form = draft ?? editable(query.data);
  const effectiveTypes = (typesText ?? form.productionActionTypes.join(", "))
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
  const effective = { ...form, productionActionTypes: effectiveTypes };
  const changed = JSON.stringify(effective) !== JSON.stringify(editable(query.data));
  const disabled = !isOwner || save.isPending;

  function set(patch: Partial<UpdateProjectPolicies>) {
    setDraft({ ...form, ...patch });
    setNotice(null);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void save.mutateAsync(effective);
  }

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div>
        <SectionHeading className="tracking-tight">Approvals</SectionHeading>
        <p className="mt-1 text-sm text-muted-foreground">
          Who approves deploys, how many approvers production needs, and how long an approval lasts.
          Changes are saved and audited. The rules marked Recorded only are not applied to approvals
          yet.
        </p>
      </div>
      {notice ? (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      ) : null}
      {save.error ? <SettingsFailure error={save.error} /> : null}
      <form aria-label="Approval policy" className="flex flex-col gap-4" onSubmit={onSubmit}>
        <label className="flex min-h-11 items-start gap-2 text-sm md:min-h-0">
          <input
            type="checkbox"
            checked={form.requireDeployApproval}
            disabled={disabled}
            onChange={(event) => set({ requireDeployApproval: event.target.checked })}
            className="mt-1"
          />
          <span>
            <span className="flex flex-wrap items-center gap-2 font-medium">
              Require approval before a deploy starts
              <RecordedOnly />
            </span>
            <span className="block text-muted-foreground">
              When applied, every deploy waits for an approver. Production waits too when two
              approvers are required.
            </span>
          </span>
        </label>
        <label className="flex min-h-11 items-start gap-2 text-sm md:min-h-0">
          <input
            type="checkbox"
            checked={form.productionTwoApprovers}
            disabled={disabled}
            onChange={(event) => set({ productionTwoApprovers: event.target.checked })}
            className="mt-1"
          />
          <span>
            <span className="flex flex-wrap items-center gap-2 font-medium">
              Two approvers for production
              <RecordedOnly />
            </span>
            <span className="block text-muted-foreground">
              When applied, the first approval is recorded and the deploy stays pending until a
              second, distinct approver decides. A decline settles at once.
            </span>
          </span>
        </label>
        <div className="flex flex-col gap-1.5">
          <span className="flex flex-wrap items-center gap-2">
            <label className="text-sm font-medium" htmlFor="production-types">
              Production action types
            </label>
            <RecordedOnly />
          </span>
          <Input
            id="production-types"
            value={typesText ?? form.productionActionTypes.join(", ")}
            disabled={disabled}
            onChange={(event) => {
              setTypesText(event.target.value);
              setNotice(null);
            }}
            placeholder="deploy.environment"
          />
          <p className="text-xs text-muted-foreground">
            Comma-separated. Only deploy.environment is production by default; other types are
            opt-in.
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="flex flex-wrap items-center gap-2">
            <label className="text-sm font-medium" htmlFor="approval-expiry">
              Approval expiry (hours)
            </label>
            <RecordedOnly />
          </span>
          <Input
            id="approval-expiry"
            type="number"
            min={1}
            max={720}
            value={form.approvalExpiryHours}
            disabled={disabled}
            onChange={(event) => set({ approvalExpiryHours: Number(event.target.value) })}
          />
          <p className="text-xs text-muted-foreground">
            When applied, a decision after this window is refused and the approval is marked
            expired.
          </p>
        </div>
        <label className="flex min-h-11 items-start gap-2 text-sm md:min-h-0">
          <input
            type="checkbox"
            checked={form.noSelfApproval}
            disabled={disabled}
            onChange={(event) => set({ noSelfApproval: event.target.checked })}
            className="mt-1"
          />
          <span>
            <span className="flex flex-wrap items-center gap-2 font-medium">
              No self-approval
              <RecordedOnly />
            </span>
            <span className="block text-muted-foreground">
              Authors cannot decide their own approvals today, whatever this is set to.
            </span>
          </span>
        </label>
        <p className="flex items-center gap-2 text-sm">
          <Badge variant="outline">Always on</Badge>
          Nobody decides a change they started, whatever the policy says.
        </p>
        {!isOwner ? (
          <p className="text-sm text-muted-foreground">Only an owner can change this policy.</p>
        ) : null}
        <div>
          <Button type="submit" disabled={disabled || !changed}>
            {save.isPending ? "Saving…" : "Save approval policy"}
          </Button>
        </div>
      </form>
    </div>
  );
}

/** The spend policy section: pause behaviour and alert thresholds. */
export function SpendPolicySection({
  projectId,
  isOwner,
}: {
  projectId: string;
  isOwner: boolean;
}) {
  const queryClient = useQueryClient();
  const query = useQuery(projectPoliciesQuery(projectId));
  const [draft, setDraft] = useState<UpdateProjectPolicies | null>(null);
  const [thresholdsText, setThresholdsText] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (input: UpdateProjectPolicies) => updateProjectPolicies(projectId, input),
    onSuccess: (value) => {
      setDraft(null);
      setThresholdsText(null);
      setNotice("Spend policy saved and audited.");
      queryClient.setQueryData(projectPoliciesQuery(projectId).queryKey, value);
    },
  });

  if (query.isPending) return <LoadingState label="Loading spend policy" rows={4} />;
  if (query.isError)
    return <SettingsFailure error={query.error} retry={() => void query.refetch()} />;
  const form = draft ?? editable(query.data);
  const effectiveThresholds = (thresholdsText ?? form.alertThresholds.join(", "))
    .split(",")
    .map((entry) => Number(entry.trim()))
    .filter((entry) => Number.isInteger(entry) && entry >= 1 && entry <= 100);
  const effective = {
    ...form,
    alertThresholds: [...new Set(effectiveThresholds)].sort((a, b) => a - b).slice(0, 5),
  };
  const changed = JSON.stringify(effective) !== JSON.stringify(editable(query.data));
  const disabled = !isOwner || save.isPending;

  function set(patch: Partial<UpdateProjectPolicies>) {
    setDraft({ ...form, ...patch });
    setNotice(null);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void save.mutateAsync(effective);
  }

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div>
        <SectionHeading className="tracking-tight">Spend and budgets</SectionHeading>
        <p className="mt-1 text-sm text-muted-foreground">
          The monthly limit and per-run cap live with billing. The settings below are saved and
          audited, but they do not change runs or alerts yet.
        </p>
      </div>
      {notice ? (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      ) : null}
      {save.error ? <SettingsFailure error={save.error} /> : null}
      <form aria-label="Spend policy" className="flex flex-col gap-4" onSubmit={onSubmit}>
        <label className="flex min-h-11 items-start gap-2 text-sm md:min-h-0">
          <input
            type="checkbox"
            checked={form.pauseAtCap}
            disabled={disabled}
            onChange={(event) => set({ pauseAtCap: event.target.checked })}
            className="mt-1"
          />
          <span>
            <span className="flex flex-wrap items-center gap-2 font-medium">
              Pause new runs at the monthly cap
              <RecordedOnly />
            </span>
            <span className="block text-muted-foreground">
              When applied, a run that would cross the monthly limit is refused before it reserves
              credits. Running work finishes.
            </span>
          </span>
        </label>
        <div className="flex flex-col gap-1.5">
          <span className="flex flex-wrap items-center gap-2">
            <label className="text-sm font-medium" htmlFor="alert-thresholds">
              Alert thresholds (percent of monthly limit)
            </label>
            <RecordedOnly />
          </span>
          <Input
            id="alert-thresholds"
            value={thresholdsText ?? form.alertThresholds.join(", ")}
            disabled={disabled}
            onChange={(event) => {
              setThresholdsText(event.target.value);
              setNotice(null);
            }}
            placeholder="80, 100"
          />
          <p className="text-xs text-muted-foreground">Comma-separated, 1 to 100.</p>
        </div>
        {!isOwner ? (
          <p className="text-sm text-muted-foreground">Only an owner can change this policy.</p>
        ) : null}
        <div>
          <Button type="submit" disabled={disabled || !changed}>
            {save.isPending ? "Saving…" : "Save spend policy"}
          </Button>
        </div>
      </form>
    </div>
  );
}

/** The agents policy section: model access, allowed models, and sandbox bounds. */
export function AgentsPolicySection({
  projectId,
  isOwner,
}: {
  projectId: string;
  isOwner: boolean;
}) {
  const queryClient = useQueryClient();
  const query = useQuery(projectPoliciesQuery(projectId));
  const [draft, setDraft] = useState<UpdateProjectPolicies | null>(null);
  // Tracked on its own: the other fields are always sent, but the parallel limit is
  // sent only when the operator touched it, so a save of any other field cannot
  // pin a copy of the project's concurrent run limit into the policy row.
  const [limitTouched, setLimitTouched] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (input: UpdateProjectPolicies) => updateProjectPolicies(projectId, input),
    onSuccess: (value) => {
      setDraft(null);
      setLimitTouched(false);
      setNotice("Agent policy saved and audited.");
      queryClient.setQueryData(projectPoliciesQuery(projectId).queryKey, value);
    },
  });

  if (query.isPending) return <LoadingState label="Loading agent policy" rows={4} />;
  if (query.isError)
    return <SettingsFailure error={query.error} retry={() => void query.refetch()} />;
  const form = draft ?? editable(query.data);
  const changed = JSON.stringify(form) !== JSON.stringify(editable(query.data));
  const disabled = !isOwner || save.isPending;

  function set(patch: Partial<UpdateProjectPolicies>) {
    setDraft({ ...form, ...patch });
    setNotice(null);
  }

  function toggleModel(tier: "standard" | "deep-reasoning") {
    const allowed = form.allowedModels.includes(tier)
      ? form.allowedModels.filter((entry) => entry !== tier)
      : [...form.allowedModels, tier];
    set({ allowedModels: allowed });
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (form.allowedModels.length === 0) return;
    // Send the parallel limit only when the operator changed it. Sending the loaded
    // value unconditionally would write a copy into the policy row, after which a
    // later project-level limit change would no longer flow through. `draft` is null
    // until the first edit, so an untouched form has nothing to send.
    const payload: UpdateProjectPolicies = { ...form };
    if (!limitTouched) delete payload.maxParallelRuns;
    void save.mutateAsync(payload);
  }

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div>
        <SectionHeading className="tracking-tight">Agents and models</SectionHeading>
        <p className="mt-1 text-sm text-muted-foreground">
          Which models runs may use, how many runs may overlap, and how long a sandbox lives.
          Changes are saved and audited. The rules marked Recorded only are not applied to runs yet.
        </p>
      </div>
      {notice ? (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      ) : null}
      {save.error ? <SettingsFailure error={save.error} /> : null}
      <form aria-label="Agent policy" className="flex flex-col gap-4" onSubmit={onSubmit}>
        <div className="flex flex-col gap-1.5">
          <span className="flex flex-wrap items-center gap-2">
            <label className="text-sm font-medium" htmlFor="model-access">
              Default model access
            </label>
            <RecordedOnly />
          </span>
          <select
            id="model-access"
            value={form.modelAccess}
            disabled={disabled}
            onChange={(event) =>
              set({ modelAccess: event.target.value as UpdateProjectPolicies["modelAccess"] })
            }
            className={settingsSelectClass}
          >
            <option value="credits">Credits</option>
            <option value="byok">Bring your own key</option>
          </select>
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="flex flex-wrap items-center gap-2 text-sm font-medium">
            Allowed models
            <RecordedOnly />
          </legend>
          {(["standard", "deep-reasoning"] as const).map((tier) => (
            <label key={tier} className="flex min-h-11 items-center gap-2 text-sm md:min-h-0">
              <input
                type="checkbox"
                checked={form.allowedModels.includes(tier)}
                disabled={disabled}
                onChange={() => toggleModel(tier)}
              />
              {tier === "standard" ? "Standard" : "Deep reasoning"}
            </label>
          ))}
          {form.allowedModels.length === 0 ? (
            <p role="alert" className="text-sm text-danger">
              At least one model must stay allowed.
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            When applied, a run asking for any other model is refused before it reserves credits.
          </p>
        </fieldset>
        <label className="flex min-h-11 items-start gap-2 text-sm md:min-h-0">
          <input
            type="checkbox"
            checked={form.readOnlyDefault}
            disabled={disabled}
            onChange={(event) => set({ readOnlyDefault: event.target.checked })}
            className="mt-1"
          />
          <span>
            <span className="flex flex-wrap items-center gap-2 font-medium">
              Read-only by default
              <RecordedOnly />
            </span>
            <span className="block text-muted-foreground">
              When applied, agents start without write access until the flow grants it.
            </span>
          </span>
        </label>
        <div className="flex flex-col gap-1.5">
          <span className="flex flex-wrap items-center gap-2">
            <label className="text-sm font-medium" htmlFor="parallel-runs">
              Parallel runs
            </label>
            <RecordedOnly />
          </span>
          <Input
            id="parallel-runs"
            type="number"
            min={1}
            max={100}
            value={form.maxParallelRuns}
            disabled={disabled}
            onChange={(event) => {
              setLimitTouched(true);
              set({ maxParallelRuns: Number(event.target.value) });
            }}
          />
          <p className="text-xs text-muted-foreground">
            When applied, new runs past this count wait until an active run finishes.
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="flex flex-wrap items-center gap-2">
            <label className="text-sm font-medium" htmlFor="sandbox-minutes">
              Sandbox time limit (minutes)
            </label>
            <RecordedOnly />
          </span>
          <Input
            id="sandbox-minutes"
            type="number"
            min={1}
            max={1440}
            value={form.sandboxMinutes}
            disabled={disabled}
            onChange={(event) => set({ sandboxMinutes: Number(event.target.value) })}
          />
        </div>
        {!isOwner ? (
          <p className="text-sm text-muted-foreground">Only an owner can change this policy.</p>
        ) : null}
        <div>
          <Button type="submit" disabled={disabled || !changed || form.allowedModels.length === 0}>
            {save.isPending ? "Saving…" : "Save agent policy"}
          </Button>
        </div>
      </form>
    </div>
  );
}
